import React, { useState, useEffect, useCallback } from 'react';
import { chatsApi, ChatType } from '../../api/chats';
import { profilesApi } from '../../api/profiles';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSearch from '../controls/HrnbSearch';
import '../../css/chats/ForwardMessageModal.css';

/**
 * Модальное окно «Поделиться в чат» — отправляет ссылку на пост в выбранный чат
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} props.postUrl - Полный URL поста
 * @param {string} props.postTitle - Заголовок поста
 */
export default function ShareToChatModal({ isOpen, onClose, postUrl, postTitle }) {
    const [chats, setChats] = useState([]);
    const [filteredChats, setFilteredChats] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingChats, setLoadingChats] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [profileCache, setProfileCache] = useState({});

    const fetchChats = useCallback(async () => {
        setLoadingChats(true);
        setError(null);
        try {
            const list = await chatsApi.listChats();
            setChats(list);
            setFilteredChats(list);

            // Загружаем профили для DIRECT чатов
            const directChats = list.filter(c => c.chatType === ChatType.DIRECT);
            const profileIds = directChats
                .flatMap(c => c.participantIds || c.participants || [])
                .filter(Boolean);

            const uniqueProfileIds = [...new Set(profileIds)];
            if (uniqueProfileIds.length > 0) {
                const cache = {};
                await Promise.all(
                    uniqueProfileIds.map(async (pid) => {
                        try {
                            const resp = await profilesApi.get(pid);
                            cache[pid] = resp.data || resp;
                        } catch (e) { /* ignore */ }
                    })
                );
                setProfileCache(cache);
            }
        } catch (err) {
            setError(err.message || 'Не удалось загрузить чаты');
        } finally {
            setLoadingChats(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchChats();
        }
    }, [isOpen, fetchChats]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredChats(chats);
            return;
        }
        const q = searchQuery.toLowerCase();
        const filtered = chats.filter(chat => {
            const title = (chat.title || '').toLowerCase();
            if (title.includes(q)) return true;
            if (chat.chatType === ChatType.DIRECT) {
                const pids = chat.participantIds || chat.participants || [];
                for (const pid of pids) {
                    const profile = profileCache[pid];
                    if (profile?.displayName?.toLowerCase().includes(q)) return true;
                }
            }
            return false;
        });
        setFilteredChats(filtered);
    }, [searchQuery, chats, profileCache]);

    const getChatDisplayName = (chat) => {
        if (chat.chatType !== ChatType.DIRECT) {
            return chat.title || 'Групповой чат';
        }
        const pids = chat.participantIds || chat.participants || [];
        for (const pid of pids) {
            const profile = profileCache[pid];
            if (profile?.displayName) return profile.displayName;
        }
        return 'Личный чат';
    };

    const getChatIcon = (chat) => {
        if (chat.chatType === ChatType.DIRECT) return '👤';
        if (chat.chatType === ChatType.BATTLE_SUPPORT) return '⚔';
        if (chat.chatType === ChatType.BATTLE_GROUP) return '⚔';
        if (chat.chatType === ChatType.BATTLE_LIVE) return '⚔';
        if (chat.chatType === ChatType.BATTLE_PARTICIPANTS) return '⚔';
        return '👥';
    };

    const handleSend = async () => {
        if (!selectedChatId) return;
        const content = `📎 ${postTitle}\n${postUrl}`;
        try {
            setSending(true);
            setError(null);
            await chatsApi.sendMessage(selectedChatId, { content });
            setSuccess('Ссылка отправлена!');
            setTimeout(() => {
                handleClose();
            }, 1200);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Не удалось отправить сообщение');
        } finally {
            setSending(false);
        }
    };

    const handleClose = () => {
        setSelectedChatId(null);
        setSearchQuery('');
        setError(null);
        setSuccess(null);
        onClose();
    };

    return (
        <HrnbModal open={isOpen} onClose={handleClose} title="Поделиться в чат">
            <div className="forward-modal">
                {loadingChats ? (
                    <div className="forward-modal__loading">
                        <HrnbLoader />
                    </div>
                ) : (
                    <>
                        {success && (
                            <HrnbAlert variant="success" onClose={() => setSuccess(null)}>
                                {success}
                            </HrnbAlert>
                        )}

                        {error && (
                            <HrnbAlert variant="danger" onClose={() => setError(null)}>
                                {error}
                            </HrnbAlert>
                        )}

                        <div className="forward-modal__search">
                            <HrnbSearch
                                placeholder="Поиск чата..."
                                value={searchQuery}
                                onChange={setSearchQuery}
                            />
                        </div>

                        <div className="forward-modal__list">
                            {filteredChats.length === 0 ? (
                                <div className="forward-modal__empty">
                                    {searchQuery ? 'Чаты не найдены' : 'Нет доступных чатов'}
                                </div>
                            ) : (
                                filteredChats.map(chat => (
                                    <div
                                        key={chat.id}
                                        className={`forward-modal__item ${selectedChatId === chat.id ? 'forward-modal__item--selected' : ''}`}
                                        onClick={() => setSelectedChatId(chat.id)}
                                    >
                                        <div className="forward-modal__item-icon">
                                            {getChatIcon(chat)}
                                        </div>
                                        <div className="forward-modal__item-info">
                                            <div className="forward-modal__item-name">
                                                {getChatDisplayName(chat)}
                                            </div>
                                        </div>
                                        <div className="forward-modal__item-check" />
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}

                <div className="forward-modal__actions">
                    <HrnbButton variant="secondary" onClick={handleClose} disabled={sending}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton
                        onClick={handleSend}
                        disabled={!selectedChatId || sending}
                    >
                        {sending ? <HrnbLoader size="sm" /> : 'Отправить'}
                    </HrnbButton>
                </div>
            </div>
        </HrnbModal>
    );
}
